import { useState } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/shared/SEO';
import { useTenant } from '@/hooks/useTenant';
import { useLanguage } from '@/hooks/useLanguage';
import { usePage } from '@/hooks/usePage';
import { submitContact } from '@/lib/api';
import toast from 'react-hot-toast';
import { PageSkeleton } from '@/components/ui/Skeleton';
import { Button as RefreshButton } from '@/components/ui/button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { contactFormSchema, ContactFormData, contactFormDefaults } from '@/schemas/contactForm.schema';
import { 
  MapPin,
  Phone,
  Mail,
  Clock,
  MessageCircle,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Send,
  ChevronDown,
  ChevronUp,
  User,
  Building,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LoadingButton, FullPageSpinner } from '@/components/ui/LoadingButton';
import { Input } from '@/components/ui/input';

// Mapeo de iconos
const iconMap = {
  'MapPin': MapPin,
  'Phone': Phone,
  'Mail': Mail,
  'Clock': Clock,
  'MessageCircle': MessageCircle,
  'Facebook': Facebook,
  'Instagram': Instagram,
  'Linkedin': Linkedin,
  'Youtube': Youtube
};

export default function ContactoPage() {
  const navigate = useNavigate();
  const { config } = useTenant();
  const { t } = useLanguage();

  // Cargar datos desde el backend usando usePage
  const { page, viewData, loading, error, refetch } = usePage('contacto');

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  // React Hook Form con validación Zod
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: contactFormDefaults,
    mode: 'onBlur' // Validate on blur for better UX
  });

  const handleHeroAction = () => {
    const ctaLink = viewData?.hero?.ctaLink || 'tel:+573001234567';
    if (ctaLink.startsWith('tel:') || ctaLink.startsWith('mailto:')) {
      window.location.href = ctaLink;
    } else {
      navigate(ctaLink);
    }
  };

  const onSubmit = async (data: ContactFormData) => {
    if (!config?.id) {
      toast.error(t('contact.errorNoCompany'));
      return;
    }

    try {
      const response = await submitContact(data, config.id);

      if (response.success) {
        toast.success(response.message || t('contact.form.success'));
        setFormSubmitted(true);
        reset(); // Reset form using react-hook-form
      } else {
        toast.error(response.message || t('contact.form.error'));
      }
    } catch (error) {
      toast.error(t('contact.form.errorDescription'));
    }
  };

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  // Generate URLs and data
  const currentUrl = `${window.location.origin}/contacto`;

  // JSON-LD Structured Data
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ContactPage",
        "@id": `${currentUrl}#webpage`,
        "url": currentUrl,
        "name": viewData?.seo?.title?.replace('{tenantName}', config?.name || '') || t('contact.title'),
        "description": viewData?.seo?.description || t('contact.subtitle'),
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": t('breadcrumb.home'),
              "item": window.location.origin
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": t('nav.contact'),
              "item": currentUrl
            }
          ]
        }
      }
    ]
  };

  // Usar datos dinámicos
  const heroTitle = viewData?.hero?.title || t('contact.title');
  const heroSubtitle = viewData?.hero?.subtitle || t('contact.subtitle');
  const heroImage = viewData?.hero?.backgroundImage || "/images/hero/contacto-hero.jpg";
  const heroShowSearch = viewData?.hero?.showSearch ?? false;
  const heroShowCTA = viewData?.hero?.showCTA ?? false;
  const heroCTAText = viewData?.hero?.ctaText || t('nav.contact');

  // SEO dinámico - Optimizado para SEO
  const seoTitle = viewData?.seo?.title?.replace('{tenantName}', config?.name || '') || t('seo.contact.title');
  const seoDescription = viewData?.seo?.description || t('seo.contact.description');
  const seoKeywords = viewData?.seo?.keywords || t('seo.contact.keywords');

  if (loading) {
    return <PageSkeleton message={t('common.loading')} />;
  }

  if (error && !viewData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t('common.errorLoadingPage')}
          </h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <RefreshButton onClick={refetch}>
            {t('common.tryAgain')}
          </RefreshButton>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={viewData?.seo?.title?.replace('{tenantName}', '') || "Contacto"}
        description={seoDescription}
        keywords={seoKeywords?.split(',').map((k: string) => k.trim())}
        canonicalPath="/contacto"
        ogImage={heroImage}
      />

      <Helmet>
        {/* Additional OG Tags */}
        <meta property="og:image:alt" content={viewData?.seo?.imageAlt || "Contacto"} />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <PageLayout
        hero={{
          title: heroTitle,
          subtitle: heroSubtitle,
          backgroundImage: heroImage,
          showSearch: heroShowSearch,
          showCTA: heroShowCTA,
          ctaText: heroCTAText,
          onCTAClick: handleHeroAction
        }}
      >
        {/* Contact Section */}
        {viewData?.contact?.enabled && (
          <section className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {viewData.contact.title}
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {viewData.contact.subtitle}
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12">
                {/* Contact Form */}
                {viewData.contact.form?.enabled && (
                  <div className="bg-gray-50 rounded-xl p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {viewData.contact.form.title}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {viewData.contact.form.subtitle}
                    </p>

                    {formSubmitted ? (
                      <div className="text-center py-8">
                        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                        <h4 className="text-xl font-semibold text-green-600 mb-2">
                          {t('contact.messageSent')}
                        </h4>
                        <p className="text-gray-600">
                          {viewData.contact.form.successMessage}
                        </p>
                        <Button
                          className="mt-4"
                          onClick={() => setFormSubmitted(false)}
                        >
                          {t('contact.sendAnotherMessage')}
                        </Button>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        {viewData.contact.form.fields?.map((field) => (
                          <div key={field.name}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </label>

                            {field.type === 'select' ? (
                              <>
                                <select
                                  {...register(field.name as keyof ContactFormData)}
                                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                                    errors[field.name as keyof ContactFormData] ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                >
                                  <option value="">{t('contact.selectOption')}</option>
                                  {field.options?.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                {errors[field.name as keyof ContactFormData] && (
                                  <p className="text-red-500 text-sm mt-1">
                                    {errors[field.name as keyof ContactFormData]?.message}
                                  </p>
                                )}
                              </>
                            ) : field.type === 'textarea' ? (
                              <>
                                <textarea
                                  {...register(field.name as keyof ContactFormData)}
                                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                                    errors[field.name as keyof ContactFormData] ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                  rows={4}
                                  placeholder={field.placeholder}
                                />
                                {errors[field.name as keyof ContactFormData] && (
                                  <p className="text-red-500 text-sm mt-1">
                                    {errors[field.name as keyof ContactFormData]?.message}
                                  </p>
                                )}
                              </>
                            ) : (
                              <>
                                <Input
                                  {...register(field.name as keyof ContactFormData)}
                                  type={field.type}
                                  placeholder={field.placeholder}
                                  className={`w-full ${
                                    errors[field.name as keyof ContactFormData] ? 'border-red-500' : ''
                                  }`}
                                />
                                {errors[field.name as keyof ContactFormData] && (
                                  <p className="text-red-500 text-sm mt-1">
                                    {errors[field.name as keyof ContactFormData]?.message}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        ))}

                        <div className="text-xs text-gray-500 mb-4">
                          {viewData.contact.form.privacyText}
                        </div>

                        <LoadingButton
                          type="submit"
                          className="w-full"
                          size="lg"
                          isLoading={isSubmitting}
                          loadingText={t('contact.form.sending')}
                        >
                          <Send className="h-5 w-5 mr-2" />
                          {viewData.contact.form.submitText}
                        </LoadingButton>
                      </form>
                    )}
                  </div>
                )}

                {/* Contact Info */}
                {viewData?.info?.enabled && (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">
                      {viewData.info.title}
                    </h3>
                    
                    {viewData.info.items?.map((item, index) => {
                      const IconComponent = iconMap[item.icon] || MapPin;
                      
                      return (
                        <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all">
                          <div className="flex items-start">
                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                              <IconComponent className="h-6 w-6 text-primary" />
                            </div>
                            
                            <div className="flex-grow">
                              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                {item.title}
                              </h4>
                              <div className="space-y-1 mb-4">
                                {item.details?.map((detail, detailIndex) => (
                                  <p key={detailIndex} className="text-gray-600">
                                    {detail}
                                  </p>
                                ))}
                              </div>
                              
                              {item.action && (
                                <Button 
                                  size="sm"
                                  onClick={() => {
                                    if (item.action.link.startsWith('tel:') || item.action.link.startsWith('mailto:')) {
                                      window.location.href = item.action.link;
                                    } else {
                                      window.open(item.action.link, '_blank');
                                    }
                                  }}
                                >
                                  {item.action.text}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Offices Section */}
        {viewData?.offices?.enabled && (
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {viewData.offices.title}
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {viewData.offices.subtitle}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {viewData.offices.locations?.map((office) => (
                  <div key={office.id} className={`bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all ${office.featured ? 'ring-2 ring-primary' : ''}`}>
                    {office.featured && (
                      <div className="bg-primary text-white text-center py-2 text-sm font-medium">
                        {t('contact.mainOffice')}
                      </div>
                    )}
                    
                    <img
                      src={office.image}
                      alt={`Oficina ${office.city}`}
                      className="w-full h-48 object-cover"
                    />
                    
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {office.name}
                      </h3>
                      <div className="flex items-center text-primary font-semibold mb-4">
                        <Building className="h-4 w-4 mr-2" />
                        {office.city}
                      </div>
                      
                      <div className="space-y-3 text-sm text-gray-600 mb-6">
                        <div className="flex items-start">
                          <MapPin className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                          <span>
                            {typeof office.address === 'string'
                              ? office.address
                              : office.address?.formattedAddress || office.address?.city || t('contact.addressNotAvailable')}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span>{office.phone}</span>
                        </div>
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span>{office.email}</span>
                        </div>
                        <div className="flex items-start">
                          <User className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                          <span>{t('contact.manager')} {office.manager}</span>
                        </div>
                      </div>

                      <div className="border-t pt-4 mb-4">
                        <h4 className="font-semibold text-gray-900 mb-2">{t('contact.schedule')}</h4>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>L-V: {office.schedule?.monday_friday}</div>
                          <div>Sáb: {office.schedule?.saturday}</div>
                          <div>Dom: {office.schedule?.sunday}</div>
                        </div>
                      </div>
                      
                      <div className="flex space-x-3">
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={() => window.location.href = `tel:${office.phone}`}
                        >
                          {t('contact.call')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(office.mapUrl, '_blank')}
                        >
                          {t('contact.viewMap')}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Social Networks */}
        {viewData?.social?.enabled && (
          <section className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {viewData.social.title}
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {viewData.social.subtitle}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
                {viewData.social.networks?.map((network) => {
                  const IconComponent = iconMap[network.icon] || MessageCircle;
                  
                  return (
                    <a
                      key={network.name}
                      href={network.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all group"
                    >
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform"
                        style={{ backgroundColor: network.color }}
                      >
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                      <h3 className="font-bold text-gray-900 mb-2">{network.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">{network.handle}</p>
                      <p className="text-xs text-gray-500">{network.description}</p>
                    </a>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        {viewData?.faq?.enabled && (
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {viewData.faq.title}
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {viewData.faq.subtitle}
                </p>
              </div>

              <div className="max-w-3xl mx-auto">
                {viewData.faq.items?.map((item, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg mb-4 overflow-hidden">
                    <button
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 focus:outline-none"
                      onClick={() => toggleFaq(index)}
                    >
                      <h3 className="font-semibold text-gray-900">{item.question}</h3>
                      {expandedFaq === index ? (
                        <ChevronUp className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                    
                    {expandedFaq === index && (
                      <div className="px-6 pb-4">
                        <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        {viewData?.cta && (
          <section
            className="py-20 bg-fixed bg-cover bg-center relative"
            style={{ backgroundImage: `url(${viewData.cta.backgroundImage})` }}
          >
            <div className="absolute inset-0 bg-primary/80"></div>
            <div className="container mx-auto px-4 relative z-10 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {viewData.cta.title}
              </h2>
              <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
                {viewData.cta.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-white text-primary hover:bg-gray-100"
                  onClick={() => {
                    if (viewData.cta.primaryButton.link.startsWith('tel:')) {
                      window.location.href = viewData.cta.primaryButton.link;
                    } else {
                      navigate(viewData.cta.primaryButton.link);
                    }
                  }}
                >
                  {viewData.cta.primaryButton.text}
                </Button>
                <Button
                  size="lg"
                  className="bg-white text-primary hover:bg-gray-100"
                  onClick={() => navigate(viewData.cta.secondaryButton.link)}
                >
                  {viewData.cta.secondaryButton.text}
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* Dynamic Sections - Render any additional sections from CMS */}
        {page?.sections?.filter(section => {
          // Exclude sections that are already rendered specifically above
          const specificSections = ['HERO', 'CONTACT', 'OFFICES', 'SOCIAL', 'FAQ', 'CTA'];
          return section.enabled && !specificSections.includes(section.type);
        }).map((section) => (
          <SectionPreview
            key={section.id}
            section={section}
            isEnabled={section.enabled}
            viewport="desktop"
          />
        ))}
      </PageLayout>
    </>
  );
}